import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import defaultShield from '@sorare/core/src/assets/club/shield_none.webp';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BodyS,
  LabelL,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { isType } from '@sorare/core/src/lib/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import type { Color } from '@sorare/core/src/style/types';

import { DomesticLeagueRanking } from '#src/shared/game/DomesticLeagueRanking/index.tsx';
import { GameStatusLabel } from '#src/shared/game/GameStatusLabel/index.tsx';
import { isGameStarted } from '#src/shared/game/lib.tsx';
import { HandInfoChip } from '#src/shared/player/HandInfoChip/index.tsx';

import type {
  BriefGame_anyGameInterface,
  BriefGame_teamInterface,
} from './__generated__/index.graphql.ts';

const Root = styled(ButtonBase)`
  width: 100%;
  text-align: left;
  cursor: default;
  background: var(--c-nd-50);
  border-radius: var(--double-unit);
  padding: var(--double-unit);

  @media ${tabletAndAbove} {
    &.withHoverStyles {
      padding: var(--double-unit);
      cursor: pointer;
      border-radius: var(--double-unit);

      &:hover {
        background-color: var(--c-nd-50);
      }
    }
  }
`;

const StyledImg = styled.img`
  object-fit: contain;
  width: var(--double-unit);
`;
const StyledHorizontal = styled(Horizontal)`
  align-items: baseline;
`;

const ProbablePitcherSubline = styled(Horizontal)`
  margin-left: var(--triple-unit);
`;

const Games = styled(Vertical)`
  &.reversed {
    flex-direction: column-reverse;
  }
`;

const getTeamTextColor = (
  game: BriefGame_anyGameInterface,
  teamSlug?: string
): Color => {
  return !game.winner || game.winner?.slug === teamSlug
    ? 'var(--c-white)'
    : 'var(--c-nd-400)';
};

const LOGO_SIZE = '16px';

type Props = {
  game: BriefGame_anyGameInterface;
  team?: BriefGame_teamInterface;
  onClick?: () => void;
};
export const BriefGame = ({ game, team, onClick }: Props) => {
  const { sport } = useSportContext();
  const hasGameStarted = isGameStarted(game.status);
  const isUsSports = sport !== Sport.FOOTBALL;

  const { homeTeam, homeScore, awayTeam, awayScore } = game;

  const isBaseballGame = isType(game, 'GameOfBaseball');

  // respect logic above, fake the homePitcher is awayPitcher and vice versa

  const [awayPitcher, homePitcher] = isBaseballGame
    ? [game.awayProbablePitcher, game.homeProbablePitcher]
    : [];
  const useTeamShortName = isBaseballGame;

  return (
    <Root
      as={onClick ? 'button' : 'div'}
      onClick={onClick}
      className={classNames({ withHoverStyles: !!onClick })}
    >
      <Vertical gap={1.5}>
        <LabelM bold color="var(--c-nd-600)">
          <GameStatusLabel game={game} />
        </LabelM>
        <Games gap={1} className={classNames({ reversed: isUsSports })}>
          <SBHorizontal>
            <Vertical gap={0.5}>
              <Horizontal>
                <StyledImg
                  src={homeTeam?.pictureUrl || defaultShield}
                  alt=""
                  width={LOGO_SIZE}
                  height={LOGO_SIZE}
                />
                <StyledHorizontal>
                  <LabelL bold color={getTeamTextColor(game, homeTeam?.slug)}>
                    {useTeamShortName ? homeTeam?.shortName : homeTeam?.name}
                  </LabelL>
                  {!!homeTeam && (
                    <LabelM>
                      <DomesticLeagueRanking
                        game={game}
                        team={homeTeam}
                        colored={homeTeam.slug !== team?.slug}
                      />
                    </LabelM>
                  )}
                </StyledHorizontal>
              </Horizontal>
              {!!isBaseballGame && !!homePitcher && (
                <ProbablePitcherSubline>
                  <BodyS as="span">{homePitcher.displayName}</BodyS>
                  <HandInfoChip position="pitching" player={homePitcher} />
                </ProbablePitcherSubline>
              )}
            </Vertical>
            <BodyM
              as="p"
              bold
              color={
                hasGameStarted
                  ? getTeamTextColor(game, homeTeam?.slug)
                  : 'var(--c-nd-600)'
              }
            >
              {hasGameStarted ? homeScore : '-'}
            </BodyM>
          </SBHorizontal>
          <SBHorizontal>
            <Vertical gap={0.5}>
              <Horizontal>
                <StyledImg
                  src={awayTeam?.pictureUrl || defaultShield}
                  alt=""
                  width={LOGO_SIZE}
                  height={LOGO_SIZE}
                />
                <StyledHorizontal>
                  <LabelL bold color={getTeamTextColor(game, awayTeam?.slug)}>
                    {useTeamShortName ? awayTeam?.shortName : awayTeam?.name}
                  </LabelL>
                  {!!awayTeam && (
                    <LabelM>
                      <DomesticLeagueRanking
                        game={game}
                        team={awayTeam}
                        colored={awayTeam.slug !== team?.slug}
                      />
                    </LabelM>
                  )}
                </StyledHorizontal>
              </Horizontal>
              {!!isBaseballGame && !!awayPitcher && (
                <ProbablePitcherSubline>
                  <BodyS as="span">{awayPitcher.displayName}</BodyS>
                  <HandInfoChip position="pitching" player={awayPitcher} />
                </ProbablePitcherSubline>
              )}
            </Vertical>
            <BodyM
              as="p"
              bold
              color={
                hasGameStarted
                  ? getTeamTextColor(game, awayTeam?.slug)
                  : 'var(--c-nd-600)'
              }
            >
              {hasGameStarted ? awayScore : '-'}
            </BodyM>
          </SBHorizontal>
        </Games>
      </Vertical>
    </Root>
  );
};

const teamFragment = gql`
  fragment BriefGame_teamInterface on TeamInterface {
    slug
    name
    shortName
    pictureUrl
    ... on NationalTeam {
      slug
      country {
        slug
        code
      }
    }
    ...DomesticLeagueRanking_teamInterface
  }
  ${DomesticLeagueRanking.fragments.teamInterface}
` as TypedDocumentNode<BriefGame_teamInterface>;

BriefGame.fragments = {
  anyGameInterface: gql`
    fragment BriefGame_anyGameInterface on AnyGameInterface {
      id
      status: statusTyped
      homeScore
      homeTeam {
        slug
        ...BriefGame_teamInterface
      }
      awayScore
      awayTeam {
        slug
        ...BriefGame_teamInterface
      }
      winner {
        slug
      }
      competition {
        slug
        format
      }
      ... on GameOfBaseball {
        id
        homeProbablePitcher {
          slug
          displayName
          ...HandInfoChip_BaseballPlayer
        }
        awayProbablePitcher {
          slug
          displayName
          ...HandInfoChip_BaseballPlayer
        }
      }
      ...GameStatusLabel_anyGameInterface
      ...DomesticLeagueRanking_anyGameInterface
    }
    ${GameStatusLabel.fragments.anyGameInterface}
    ${DomesticLeagueRanking.fragments.anyGameInterface}
    ${teamFragment}
    ${HandInfoChip.fragments.BaseballPlayer}
  ` as TypedDocumentNode<BriefGame_anyGameInterface>,
  teamInterface: teamFragment,
};
